import React from 'react';
import styled, { css } from 'styled-components';

import Link from '@/components/Link';
import { colors } from '@/constants/theme';
import { useTypedSelector } from '@/store/store';
import { media } from '@/utils/mixin';
import { selectSinglePost } from '@/store/reducers/single-post';

const ExploreAndTry = React.memo(() => {
  const page = useTypedSelector(selectSinglePost);

  if (!page || !page.ctaButtons || page.ctaButtons.length === 0) {
    return null;
  }

  const renderButtons = page.ctaButtons.map((btn) => {
    return (
      <Button
        to={btn.link}
        target={`${btn.mode === 'linkNewTab' ? '_blank' : '_self'}`}
      >
        {btn.label}
      </Button>
    );
  });

  return (
    <Component isOneButton={page.ctaButtons.length === 1}>
      <ComponentContainer>
        <Label>{page.ctaTitle}</Label>
        <Buttons>{renderButtons}</Buttons>
      </ComponentContainer>
    </Component>
  );
});

export default ExploreAndTry;

const Buttons = styled.span`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  justify-content: space-around;
`;

const Button = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  appearance: none;

  padding: 8px 15px;

  height: 32px;

  background: ${colors.main};
  border: 2px solid ${colors.main};
  border-radius: 12px;

  font-weight: 500;
  font-size: 14px;
  line-height: 16px;

  text-align: center;
  text-transform: uppercase;

  color: ${colors.white};

  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;

  &:not(:first-child) {
    margin-right: 15px;
  }

  &:hover {
    border-color: #f2f2f2;
    background-color: #f2f2f2;
  }

  &:active {
    border-color: #e5e5e5;
    background-color: #e5e5e5;
  }

  ${media.mobileUp(
    css`
      color: ${colors.black200};
      background: ${colors.white};
      border: 2px solid ${colors.white};
      border-radius: 20px;
    `
  )};

  @media (min-width: 1600px) {
    padding: 20px 57px;

    font-size: 20px;
    line-height: 26px;

    &:not(:first-child) {
      margin-right: 68px;
    }
  }
`;

const Component = styled.div<{ isOneButton: boolean }>`
  position: sticky;
  top: calc(100vh - 60px);
  z-index: 100;
  display: flex;
  padding: 20px 0;
  height: 60px;
  background: ${(props) => props.theme.white};

  ${media.mobile(
    css`
      position: initial;
      background: ${(props) => props.theme.white};
      margin-bottom: -40px;
    `
  )}

  ${media.mobileUp(
    css`
      top: 140px;
      background: ${colors.main};
    `
  )}
  
  ${media.tabletUp(
    css`
      top: 60px;
    `
  )}

  ${(props) =>
    props.isOneButton
      ? css`
          ${media.mobile(css`
            ${Buttons} {
              width: 100%;
            }

            ${Button} {
              width: 100%;
            }
          `)}
        `
      : null}
`;

const ComponentContainer = styled.div`
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  max-width: 67%;

  ${media.tablet(css`
    max-width: 100%;
    align-items: center;
  `)}

  ${media.mobileUp(
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
    `
  )}
`;

const Label = styled.span`
  display: none;

  font-weight: 700;
  font-size: 20px;
  line-height: 1.4;
  text-align: center;

  color: ${colors.white};

  ${media.mobileUp(
    css`
      display: block;
    `
  )};

  @media (min-width: 1600px) {
    font-size: 26px;
    line-height: 42px;
  }
`;
